import { useId } from "react";

interface LogoProps extends React.SVGProps<SVGSVGElement> {
  title?: string;
}

interface LogoMarkProps extends LogoProps {
  /** Play the one-shot spin/pop intro. Off for static uses like bylines. */
  animate?: boolean;
}

/**
 * The mark's five shapes, drawn in a 0–100 space (the coordinate system the
 * brand file ships in). Content sits inside x 20–85, y 16–79 — deliberately
 * off-centre, the two big discs carry the weight low and the small ones lead
 * the eye in from the top. Both exports place this same group, so the icon and
 * the lockup can never drift apart. Keep the children flat: the pop animation
 * targets direct children by nth-child, and a wrapper <g> would swallow its own
 * transform.
 */
function MarkShapes() {
  return (
    <>
      <circle cx="32" cy="27" r="11" fill="#F2A93B" />
      <circle cx="66" cy="29" r="9" fill="#8B6BB0" />
      <circle cx="38" cy="61" r="18" fill="#0E4A45" />
      <circle cx="67" cy="61" r="18" fill="#D2603C" />
      <path
        d="M60.5,61 L54.41,63.41 L52,69.5 L49.59,63.41 L43.5,61 L49.59,58.59 L52,52.5 L54.41,58.59 Z"
        fill="#F4B93F"
      />
    </>
  );
}

export function LogoMark({ className, title = "Psihotron", animate = true, ...props }: LogoMarkProps) {
  return (
    <svg
      // Square window around the artwork's own bounds, so the mark fills a
      // size-N box evenly instead of sitting off-centre in the 100x100 artboard.
      viewBox="20 15 65 65"
      xmlns="http://www.w3.org/2000/svg"
      overflow="visible"
      role="img"
      aria-label={title}
      className={className}
      {...props}
    >
      <g className={animate ? "animate-logo-mark" : undefined}>
        <MarkShapes />
      </g>
    </svg>
  );
}

export function Logo({ className, title = "Psihotron", ...props }: LogoProps) {
  const clipId = useId();
  return (
    <svg
      viewBox="0 0 48 11"
      xmlns="http://www.w3.org/2000/svg"
      overflow="visible"
      fill="currentColor"
      role="img"
      aria-label={title}
      className={className}
      {...props}
    >
      <defs>
        {/* Hides the wordmark while it slides in, so it emerges from behind the mark */}
        <clipPath id={clipId}>
          <rect x="11" y="-1" width="38" height="13" />
        </clipPath>
      </defs>
      {/* The mark, scaled from its 0–100 space into the lockup's 11-unit band
          and set at the wordmark's x-height. 0.161905 = 10.2 / 63, the artwork's
          own height; the translate then lands its top-left at (0.1, 0.4). The
          placement has to sit on its own wrapper: .animate-logo-mark animates
          `transform`, and a CSS transform replaces the element's transform
          attribute outright rather than composing with it. */}
      <g transform="translate(-3.1381, -2.1905) scale(0.161905)">
        <g className="animate-logo-mark">
          <MarkShapes />
        </g>
      </g>
      {/* Wordmark in Inter */}
      <g clipPath={`url(#${clipId})`}>
        <g className="animate-logo-wordmark">
          <text
            x="11.8"
            y="5.6"
            dominantBaseline="central"
            fontFamily="var(--font-body)"
            fontSize="7.5"
            fontWeight="600"
            letterSpacing="-0.03em"
            fill="currentColor"
          >
            psihotron
          </text>
        </g>
      </g>
    </svg>
  );
}
